import { Badge, Tooltip } from 'flowbite-react';
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi';
import classNames from 'classnames';
import { MldFormatOptions, MldStatusChipInput, mldStatusChip } from './helpers';
import { mldStrings } from './strings';

export interface MldStatusChipProps {
  deal: MldStatusChipInput;
  /** Time zone and "today" for the dates in the context; tests inject them. */
  options?: MldFormatOptions;
  className?: string;
}

/**
 * The deal status as a Badge whose text always carries the state (and its
 * context, e.g. "Payment due · card declined"), so the colour is never the
 * only signal. A deal that began with a trial gets a separate "Trial sale"
 * marker. Shared by the MLD list, the deal detail and the Member › MLD tab.
 */
export const MldStatusChip = ({
  deal,
  options,
  className,
}: MldStatusChipProps) => {
  const chip = mldStatusChip(deal, options);
  return (
    <span
      className={classNames(
        'inline-flex flex-wrap items-center gap-1',
        className,
      )}
      data-testid="mld-status-chip"
    >
      <Badge
        color={chip.color}
        size="sm"
        className="w-fit"
        data-testid="mld-status-chip-status"
      >
        {chip.text}
      </Badge>
      {deal.status === 'PAYMENT_DUE' && <MldPaymentDueHelp />}
      {chip.trialSaleLabel && (
        <Badge
          color="indigo"
          size="sm"
          className="w-fit"
          data-testid="mld-status-chip-trial-sale"
        >
          {chip.trialSaleLabel}
        </Badge>
      )}
    </span>
  );
};

/**
 * The "(?)" next to "Payment due": what the status means (Chargebee dunning).
 * ABO's flowbite Tooltip opens on hover, on keyboard focus and on a tap on a
 * touch screen. The click stops at the button, so a list row does not open.
 */
export const MldPaymentDueHelp = () => (
  <Tooltip
    content={mldStrings.paymentDueHelp.text}
    className="max-w-xs whitespace-normal text-left"
    data-testid="mld-payment-due-help-tooltip"
  >
    <button
      type="button"
      aria-label={mldStrings.paymentDueHelp.label}
      onClick={(event) => event.stopPropagation()}
      className="inline-flex items-center rounded-full text-neutral-500 hover:text-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      data-testid="mld-payment-due-help"
    >
      <HiOutlineQuestionMarkCircle aria-hidden="true" className="h-4 w-4" />
    </button>
  </Tooltip>
);

/**
 * Marks every MLD screen as a staging preview, so nobody mistakes it for
 * production data (PRD "Discoverability").
 */
export const MldPreviewLabel = ({ className }: { className?: string }) => (
  <Badge
    color="purple"
    size="sm"
    className={classNames('w-fit', className)}
    title={mldStrings.previewHint}
    data-testid="mld-preview-label"
  >
    {mldStrings.previewLabel}
  </Badge>
);
